import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Trash2, ExternalLink } from "lucide-react";
import { beverageGet, beverageUpdate, beverageDelete } from "../../lib/adminApi";

const CATEGORIES = [
  "whisky", "rum", "tequila", "mezcal", "vodka", "liqueurs",
  "agave", "aperitifs", "wine", "beer", "sake", "vermouth", "amaro",
];
const PRICING_MODES = [
  { v: "POUR_ONLY", l: "Pour only" },
  { v: "BOTTLE_ONLY", l: "Bottle only" },
  { v: "BOTH", l: "Both" },
];

// Categories that are bottle-first by industry convention — UI hints, not constraints.
const BOTTLE_FIRST_CATEGORIES = new Set(["wine", "sake", "beer"]);

const numOrNull = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export default function AdminBeverageEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    beverageGet(id)
      .then((d) => setB({
        ...d,
        style_tags: d.style_tags || [],
        flavour_tags: d.flavour_tags || [],
      }))
      .catch((e) => setErr(e.response?.data?.detail || "Beverage not found"));
  }, [id]);

  if (err && !b) {
    return (
      <div className="px-5 md:px-10 py-12 max-w-3xl" data-testid="bev-editor-error">
        <p className="overline text-red-400 mb-2">Error</p>
        <p className="text-white/80">{err}</p>
        <Link to="/admin/beverages" className="inline-block mt-4 text-xs uppercase tracking-[0.22em] text-[#C17F59]">
          ← Back to Spirits Menu
        </Link>
      </div>
    );
  }

  if (!b) return <div className="px-5 md:px-10 py-12 text-white/40">Loading…</div>;

  const set = (patch) => setB({ ...b, ...patch });
  const mode = b.pricing_mode || "POUR_ONLY";
  const isBottleFirst = BOTTLE_FIRST_CATEGORIES.has(b.category);

  // Auto-derive mode preview so admin sees the impact of their price edits in real time
  const derivedMode = (() => {
    const p30 = numOrNull(b.price_30ml);
    const p60 = numOrNull(b.price_60ml);
    const pb = numOrNull(b.price_by_bottle);
    const hasPour = !!(p30 || p60);
    if (hasPour && pb) return "BOTH";
    if (pb && !hasPour) return "BOTTLE_ONLY";
    return "POUR_ONLY";
  })();

  // Client-side guard mirroring backend validation
  const validation = (() => {
    if (!b.category || !b.brand?.trim()) return "Category and brand are required";
    const p30 = numOrNull(b.price_30ml);
    const p60 = numOrNull(b.price_60ml);
    const pb = numOrNull(b.price_by_bottle);
    if (mode === "POUR_ONLY" && !(p30 || p60)) return "POUR_ONLY requires at least one of 30ml or 60ml price";
    if (mode === "BOTTLE_ONLY" && !pb) return "BOTTLE_ONLY requires bottle price";
    if (mode === "BOTH" && !((p30 || p60) && pb)) return "BOTH requires at least one pour price AND a bottle price";
    return null;
  })();

  const save = async () => {
    if (validation) { setErr(validation); return; }
    setBusy(true); setErr(null);
    try {
      const p30 = mode === "BOTTLE_ONLY" ? null : numOrNull(b.price_30ml);
      const p60 = mode === "BOTTLE_ONLY" ? null : numOrNull(b.price_60ml);
      const pb = mode === "POUR_ONLY" ? null : numOrNull(b.price_by_bottle);
      const body = {
        category: b.category,
        subcategory: b.subcategory || null,
        brand: b.brand.trim(),
        expression: b.expression || null,
        display_name: b.display_name || null,
        country: b.country || null,
        region: b.region || null,
        abv: b.abv ? Number(b.abv) : null,
        price_30ml: p30,
        price_60ml: p60,
        price_by_bottle: pb,
        pricing_mode: mode,
        availability_status: b.availability_status || "AVAILABLE",
        visibility: b.visibility || "public",
        style_tags: b.style_tags || [],
        flavour_tags: b.flavour_tags || [],
        notes: b.notes || null,
        source: b.source || "manual",
      };
      const updated = await beverageUpdate(id, body);
      setB({ ...updated, style_tags: updated.style_tags || [], flavour_tags: updated.flavour_tags || [] });
      setSaved(new Date().toLocaleTimeString());
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${b.display_name}"? This cannot be undone.`)) return;
    try {
      await beverageDelete(id);
      nav("/admin/beverages");
    } catch (e) {
      setErr(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="px-5 md:px-10 py-8 max-w-5xl" data-testid="admin-beverage-editor">
      <Link to="/admin/beverages" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/50 hover:text-[#C17F59] mb-5" data-testid="back-bev-list">
        <ArrowLeft size={12} /> All bottles
      </Link>

      <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-[#C17F59] mb-2 capitalize">{b.category}{b.subcategory ? ` · ${b.subcategory}` : ""}</p>
          <h1 className="font-display text-4xl">{b.display_name}</h1>
          <p className="text-[10px] uppercase tracking-[0.22em] text-white/35 mt-2">{b.id}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`/spirits/item/${b.id}`} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 border border-white/15 text-white/80 hover:text-[#C17F59] hover:border-[#C17F59] px-4 py-2 text-[11px] uppercase tracking-[0.22em]"
             data-testid="bev-view-public">
            <ExternalLink size={12} /> View public
          </a>
          <button onClick={save} disabled={busy || !!validation}
                  className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#D89A74] disabled:opacity-40"
                  data-testid="bev-save">
            <Save size={12} /> {busy ? "Saving…" : "Save"}
          </button>
          <button onClick={remove}
                  className="inline-flex items-center gap-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 px-3 py-2 text-[11px] uppercase tracking-[0.22em]"
                  data-testid="bev-delete">
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </header>

      {validation && (
        <div className="mb-5 border border-amber-500/30 bg-amber-500/5 text-amber-300 px-4 py-2 text-xs" data-testid="bev-validation">
          {validation}
        </div>
      )}
      {err && (
        <div className="mb-5 border border-red-500/30 bg-red-500/5 text-red-300 px-4 py-2 text-xs" data-testid="bev-error">
          {err}
        </div>
      )}
      {saved && !err && (
        <div className="mb-5 border border-green-500/30 text-green-400 px-4 py-2 text-xs" data-testid="bev-saved">
          Saved · {saved}
        </div>
      )}

      <div className="space-y-6">
        {/* Core */}
        <Section title="Core">
          <Grid>
            <Field label="Category">
              <Select value={b.category} onChange={(v) => set({ category: v })} data-testid="bev-category">
                {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#15171A] text-white">{c}</option>)}
              </Select>
            </Field>
            <Field label="Subcategory">
              <Input value={b.subcategory || ""} onChange={(v) => set({ subcategory: v })} data-testid="bev-subcategory" />
            </Field>
            <Field label="Brand">
              <Input value={b.brand || ""} onChange={(v) => set({ brand: v })} data-testid="bev-brand" />
            </Field>
            <Field label="Expression">
              <Input value={b.expression || ""} onChange={(v) => set({ expression: v })} data-testid="bev-expression" />
            </Field>
            <Field label="Display name (override)">
              <Input value={b.display_name || ""} onChange={(v) => set({ display_name: v })} data-testid="bev-display-name" />
            </Field>
            <Field label="Country">
              <Input value={b.country || ""} onChange={(v) => set({ country: v })} data-testid="bev-country" />
            </Field>
            <Field label="Region">
              <Input value={b.region || ""} onChange={(v) => set({ region: v })} data-testid="bev-region" />
            </Field>
            <Field label="ABV %">
              <Input type="number" value={b.abv ?? ""} onChange={(v) => set({ abv: v })} data-testid="bev-abv" />
            </Field>
          </Grid>
        </Section>

        {/* Pricing */}
        <Section title="Pricing">
          <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <span className="overline text-white/50">Mode</span>
              <select value={mode} onChange={(e) => set({ pricing_mode: e.target.value })}
                      className="bg-[#101113] text-white border border-white/15 focus:border-[#C17F59] focus:outline-none text-sm py-2 px-3"
                      data-testid="bev-pricing-mode">
                {PRICING_MODES.map((m) => <option key={m.v} value={m.v} className="bg-[#15171A] text-white">{m.l}</option>)}
              </select>
              {derivedMode !== mode && (
                <span className="text-[10px] uppercase tracking-[0.2em] text-amber-400" data-testid="bev-mode-mismatch">
                  Auto would be: {derivedMode}
                </span>
              )}
            </div>
            {isBottleFirst && (
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#C17F59] border border-[#C17F59]/40 px-2 py-1" data-testid="bev-bottle-first-hint">
                {b.category} typically sold by bottle
              </span>
            )}
          </div>
          <Grid>
            {mode !== "BOTTLE_ONLY" && (
              <>
                <Field label="Price · 30ml (₹)" required={mode === "POUR_ONLY" || mode === "BOTH"}>
                  <Input type="number" value={b.price_30ml ?? ""} onChange={(v) => set({ price_30ml: v })} data-testid="bev-price-30ml" placeholder="Small peg" />
                </Field>
                <Field label="Price · 60ml (₹)">
                  <Input type="number" value={b.price_60ml ?? ""} onChange={(v) => set({ price_60ml: v })} data-testid="bev-price-60ml" placeholder="Large peg · optional" />
                </Field>
              </>
            )}
            {mode !== "POUR_ONLY" && (
              <Field label="Price · Bottle (₹)" required={mode === "BOTTLE_ONLY" || mode === "BOTH"} full={mode === "BOTTLE_ONLY"}>
                <Input type="number" value={b.price_by_bottle ?? ""} onChange={(v) => set({ price_by_bottle: v })} data-testid="bev-price-bottle" placeholder="750 ml bottle" />
              </Field>
            )}
          </Grid>
          {mode === "BOTH" && (
            <p className="text-[11px] text-white/45 mt-3 leading-relaxed" data-testid="bev-pricing-hint">
              At least one pour price (30ml or 60ml) is required. If both are filled, guests see both options. Flight pricing uses 30ml (or derives 30ml from 60ml ÷ 2).
            </p>
          )}
          {mode === "POUR_ONLY" && (
            <p className="text-[11px] text-white/45 mt-3 leading-relaxed">
              At least one of 30ml or 60ml is required. Bottles aren't sold for this SKU.
            </p>
          )}
          {mode === "BOTTLE_ONLY" && (
            <p className="text-[11px] text-white/45 mt-3 leading-relaxed">
              Cellar pick — sold only by the full bottle. Excluded from tasting flights.
            </p>
          )}
        </Section>

        {/* Menu State */}
        <Section title="Menu state">
          <Grid>
            <Field label="Availability">
              <Select value={b.availability_status || "AVAILABLE"} onChange={(v) => set({ availability_status: v })} data-testid="bev-availability">
                <option value="AVAILABLE" className="bg-[#15171A] text-white">Available</option>
                <option value="NOT_AVAILABLE" className="bg-[#15171A] text-white">Not Available</option>
                <option value="HIDDEN" className="bg-[#15171A] text-white">Hidden (no public render)</option>
              </Select>
            </Field>
            <Field label="Visibility">
              <Select value={b.visibility || "public"} onChange={(v) => set({ visibility: v })} data-testid="bev-visibility">
                <option value="public" className="bg-[#15171A] text-white">Public</option>
                <option value="admin_only" className="bg-[#15171A] text-white">Admin only</option>
              </Select>
            </Field>
          </Grid>
        </Section>

        {/* Content */}
        <Section title="Content">
          <Grid>
            <Field label="Style tags (comma-separated)" full>
              <Input value={(b.style_tags || []).join(", ")}
                     onChange={(v) => set({ style_tags: v.split(",").map((t) => t.trim()).filter(Boolean) })}
                     data-testid="bev-style-tags" />
            </Field>
            <Field label="Flavour tags (comma-separated)" full>
              <Input value={(b.flavour_tags || []).join(", ")}
                     onChange={(v) => set({ flavour_tags: v.split(",").map((t) => t.trim()).filter(Boolean) })}
                     data-testid="bev-flavour-tags" />
            </Field>
            <Field label="Tasting / editorial notes" full>
              <textarea value={b.notes || ""} onChange={(e) => set({ notes: e.target.value })}
                        rows={4}
                        className="w-full bg-transparent border border-white/15 focus:border-[#C17F59] focus:outline-none text-white text-sm py-2 px-3 resize-y"
                        data-testid="bev-notes" />
            </Field>
          </Grid>
        </Section>

        <div className="text-[10px] text-white/35 uppercase tracking-[0.22em]" data-testid="bev-metadata">
          Source: {b.source || "manual"} · Created: {b.created_at?.slice(0, 19).replace("T", " ") || "—"} · Updated: {b.updated_at?.slice(0, 19).replace("T", " ") || "—"}
          {b.import_batch_id && <> · Import: {b.import_batch_id}</>}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-[#15171A] border border-white/8 p-5 md:p-6">
      <p className="overline text-white/45 mb-4">{title}</p>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, required, full, children }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="overline text-white/50 mb-1 block">
        {label}{required ? <span className="text-[#C17F59]"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

function Input({ value, onChange, type = "text", ...rest }) {
  return (
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
           className="w-full bg-transparent border border-white/15 focus:border-[#C17F59] focus:outline-none text-white text-sm py-2 px-3"
           {...rest} />
  );
}

function Select({ value, onChange, children, ...rest }) {
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
            className="w-full bg-[#101113] border border-white/15 focus:border-[#C17F59] focus:outline-none text-white text-sm py-2 px-3"
            {...rest}>
      {children}
    </select>
  );
}
